import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import { Button, Field, Notice, Section } from '../../design-system/components';
import { money, numberPt } from './helpers';
import type { ConstructionData, QuestionOption, QuestionSection } from '../../types/contracts';

const REPETITION_DESCRIPTION =
  'Aplica os coeficientes configurados conforme a quantidade e a posição de cada repetição.';
const NO_REDUCTION_DESCRIPTION =
  'Soma integralmente as áreas informadas, sem redução por repetição.';
const blankFloor = () => ({
  name: 'Pavimento',
  area_m2: 0,
  quantity: 1,
  repetition_type: 'none',
  observations: '',
  show_observation: false,
});
const blankGroup = () => ({ name: 'Grupo', element_type: '', quantity: 1 });
const blankInstallment = (index: number) => ({
  label: `Parcela ${index + 1}`,
  percentage: '',
  amount: '',
  moment_type: 'final_delivery',
  moment_date: '',
  moment_days: '',
  moment_event: 'a entrega final',
  moment_stage: '',
  moment_text: '',
});
const defaultPayment = () => ({
  installment_count: 1,
  definition_mode: 'percentage',
  payment_method: 'Pix',
  validity_days: 15,
  installments: [{ ...blankInstallment(0), percentage: '100' }],
});
export const PROPOSAL_RESPONSIBLES = ['João Marcelo Cruz Liguori', 'José Elmo Quezado Cruz'] as const;
export function isProposalResponsible(value: unknown): boolean { return PROPOSAL_RESPONSIBLES.some((name) => name === value); }

function amountToCents(value: any): number {
  const raw = String(value ?? '0').replace('R$', '').trim();
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const parsed = Number(normalized || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}
function centsToBrl(cents: number): string {
  return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
export function equalInstallmentValues(total: any, count: number): string[] {
  const quantity = Math.max(1, Math.trunc(Number(count) || 1));
  const totalCents = amountToCents(total);
  const regular = Math.floor(totalCents / quantity + 0.5);
  const rows = Array.from({ length: quantity }, () => regular);
  rows[quantity - 1] = totalCents - rows.slice(0, -1).reduce((sum, value) => sum + value, 0);
  return rows.map(centsToBrl);
}
export function paymentWithCount(source: any, count: number) {
  const nextCount = Math.max(1, count);
  const current = Array.isArray(source?.installments) ? source.installments : [];
  if (nextCount === 1) {
    const first = { ...blankInstallment(0), ...(current[0] || {}) };
    return {
      ...source,
      installment_count: 1,
      definition_mode: 'percentage',
      payment_method: source?.payment_method || 'Pix',
      validity_days: source?.validity_days ?? 15,
      installments: [{ ...first, label: 'Parcela 1', percentage: '100', amount: '' }],
    };
  }
  return {
    ...source,
    installment_count: nextCount,
    installments: Array.from({ length: nextCount }, (_, index) => ({ ...blankInstallment(index), ...(current[index] || {}) })),
  };
}

function normalizeMoneyInput(value: string): string | null {
  const text = value.trim();
  if (!text) return null;
  return text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
}
function selectedItems(inputs: Record<string, any>): string[] {
  const exclusive = [
    'geometry', 'spans', 'transitions', 'existing', 'architecture', 'disciplines',
    'inspection_size', 'manifestation_diversity', 'structural_documentation', 'access_condition', 'report_content',
    'reinforcement_documentation', 'reinforcement_condition', 'reinforcement_modeling', 'reinforcement_use_change',
    'previous_opinion_admissibility',
  ];
  const accumulative = [
    'super_accumulative', 'shallow_items', 'deep_items', 's5_analysis_items', 's5_execution_items',
    'reinforcement_survey_items', 'reinforcement_solution_items', 'reinforcement_execution_items', 'reinforcement_manual_items',
  ];
  return [
    ...exclusive.map((key) => inputs[key]).filter(Boolean),
    ...accumulative.flatMap((key) => inputs[key] || []),
  ];
}
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}
function setNested(source: any, path: string, value: any) {
  const next = clone(source || {});
  const keys = path.split('.');
  let cursor = next;
  keys.slice(0, -1).forEach((key) => {
    cursor[key] = cursor[key] && typeof cursor[key] === 'object' ? cursor[key] : {};
    cursor = cursor[key];
  });
  cursor[keys[keys.length - 1]] = value;
  return next;
}
function pct(value: any) {
  return String(Number(value || 0) * 100).replace('.', ',');
}
function ratioInput(value: string) {
  return String((Number(normalizeMoneyInput(value) || 0) / 100).toFixed(6));
}
function normalizeOption(option: QuestionOption | string): QuestionOption {
  if (typeof option === 'string') return { id: option, label: option };
  return {
    id: String(option?.id || ''),
    label: String(option?.label || option?.id || ''),
    points: option?.points,
    manual: Boolean(option?.manual),
  };
}

export function GuidedConstruction({ id, onComplete, openFinancialInitially = false, onFinancialInitialOpenHandled }: { id: string; onComplete: () => void; openFinancialInitially?: boolean; onFinancialInitialOpenHandled?: () => void }) {
  const [data, setData] = useState<ConstructionData | null>(null);
  const [financial, setFinancial] = useState<any>(null);
  const [inputs, setInputs] = useState<Record<string, any>>({
    floor_groups: [blankFloor()],
    reinforcement_groups: [blankGroup()],
    deadline_mode: 'normal',
    deadline_basis: 'business_after_compatibility',
    deadline_days: 30,
    included_meetings: 0,
    include_meetings_in_document: false,
    revisions_before: 0,
    revisions_after: 0,
    revisions_manual: false,
    included_visits: 0,
    included_visit_hours: 0,
    include_visits_in_document: false,
    visit_hours: 0,
    outside_salvador: false,
    proposal_responsible: '',
  });
  const [busy, setBusy] = useState(false);
  const [financialUpdating, setFinancialUpdating] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [financialModalOpen, setFinancialModalOpen] = useState(false);
  const loaded = useRef(false);
  const adjustButtonRef = useRef<HTMLButtonElement>(null);
  const initialFinancialOpenHandled = useRef(false);
  async function load() {
    try {
      const response = await api.get<ConstructionData>(`/proposals/${id}/construction`);
      const state = await api.get<any>(`/proposals/${id}/financial-state`);
      setData(response);
      setFinancial(state);
      const defaults = response.questions.defaults || {};
      const stored = response.calculation?.input_json || {};
      setInputs({
        ...defaults,
        deadline_mode: 'normal',
        deadline_basis: 'business_after_compatibility',
        deadline_days: 30,
        include_meetings_in_document: false,
        include_visits_in_document: false,
        proposal_responsible: '',
        ...stored,
        floor_groups: (stored.floor_groups || [blankFloor()]).map((row: any) => ({
          ...row,
          repetition_type: row.repetition_type || 'none',
          show_observation: Boolean(row.show_observation || row.observations),
        })),
        reinforcement_groups: stored.reinforcement_groups || [blankGroup()],
      });
      loaded.current = true;
    } catch (cause: any) {
      setError(cause.message);
    }
  }
  useEffect(() => {
    loaded.current = false;
    load();
  }, [id]);
  useEffect(() => {
    initialFinancialOpenHandled.current = false;
  }, [id]);
  useEffect(() => {
    if (data && openFinancialInitially && !initialFinancialOpenHandled.current) {
      initialFinancialOpenHandled.current = true;
      setFinancialModalOpen(true);
      onFinancialInitialOpenHandled?.();
    }
  }, [data, openFinancialInitially]);
  async function calculate(next = inputs, quiet = false) {
    if (!loaded.current) return;
    setBusy(true);
    setFinancialUpdating(true);
    if (!quiet) setError('');
    try {
      const payload = { ...next, selected_items: selectedItems(next) };
      const response = await api.put<any>(`/proposals/${id}/construction`, { inputs: payload });
      setData((old) =>
        old
          ? {
              ...old,
              calculation: {
                input_json: response.inputs,
                result_json: response.result,
                automatic_price: response.result.final_price,
                requires_manual: Number(response.result.requires_manual_review),
                rule_version: response.result.rule_version,
                rule_classification: response.result.rule_classification,
              },
              blocks: response.blocks,
              proposal: {
                ...old.proposal,
                pricing_json: response.pricing,
                payment_json: response.payment,
                grossup_json: response.grossup,
                content_conflicts: response.content_conflicts,
              },
            }
          : old,
      );
      setFinancial(response.financial_state);
      setSaved('Alterações salvas e quadro financeiro atualizado.');
    } catch (cause: any) {
      if (!quiet) setError(cause.message);
    } finally {
      setBusy(false);
      setFinancialUpdating(false);
    }
  }
  useEffect(() => {
    if (!loaded.current) return;
    setFinancialUpdating(true);
    const timer = window.setTimeout(() => calculate(inputs, true), 650);
    return () => window.clearTimeout(timer);
  }, [JSON.stringify(inputs)]);
  function set(key: string, value: any) {
    setInputs((old) => ({ ...old, [key]: value }));
  }
  const result = data?.calculation?.result_json;
  const pricing = data?.proposal.pricing_json || {};
  const manualPending = Boolean(pricing.manual_pending || pricing.price_unavailable);
  const commercialConfigured = financial?.confirmation_status === 'confirmed' || financial?.migration_status === 'kept_previous';
  async function complete() {
    if (!isProposalResponsible(inputs.proposal_responsible)) {
      setError('Selecione o responsável pela proposta antes de concluir a construção.');
      return;
    }
    if (manualPending) {
      setError('A construção não pode ser concluída enquanto o preço técnico aguarda definição manual.');
      return;
    }
    if (!commercialConfigured) {
      setError('Confirme preço e pagamento antes de concluir a construção.');
      return;
    }
    try {
      await calculate(inputs);
      await api.put(`/proposals/${id}/stage`, { stage: 'editar' });
      onComplete();
    } catch (cause: any) {
      setError(cause.message);
    }
  }
  async function decideMigration(decision: 'keep_previous' | 'update_current') {
    setBusy(true);
    setError('');
    try {
      const response = await api.post<any>(`/proposals/${id}/financial-migration-decision`, { decision });
      setFinancial(response.financial_state);
      if (decision === 'update_current') await load();
      setSaved(decision === 'keep_previous' ? 'Parâmetros anteriores preservados.' : 'Perfil vigente aplicado e proposta recalculada.');
    } catch (cause: any) {
      setError(cause.message);
    } finally {
      setBusy(false);
    }
  }
  function applyFinancialUpdate(response: any) {
    if (response.construction) {
      setData(response.construction);
    } else {
      setData((old) =>
        old
          ? {
              ...old,
              blocks: response.blocks || old.blocks,
              proposal: {
                ...old.proposal,
                pricing_json: response.pricing || response.financial_state?.pricing || old.proposal.pricing_json,
                payment_json: response.payment || old.proposal.payment_json,
                grossup_json: response.grossup || old.proposal.grossup_json,
                content_conflicts: response.content_conflicts ?? old.proposal.content_conflicts,
              },
            }
          : old,
      );
    }
    setFinancial(response.financial_state || response);
    setFinancialUpdating(false);
  }
  if (!data) return <main className="loading">Carregando construção da proposta...</main>;
  return (
    <div className="construction-grid">
      <div className="construction-form">
        <header className="flow-head">
          <span className="eyebrow">CONSTRUIR</span>
          <h2>Construção da proposta</h2>
          <p>Responda às etapas. O quadro lateral usa o estado financeiro global e reage sem depender da abertura de outra tela.</p>
        </header>
        {error && (
          <Notice tone="danger" title="Não foi possível atualizar">
            {error}
          </Notice>
        )}
        {saved && (
          <div className="inline-save" aria-live="polite">
            {saved}
          </div>
        )}
        {financial?.migration_decision_required && (
          <Notice tone="warning" title="Esta proposta usa uma versão anterior de parâmetros">
            <p>Escolha explicitamente se os valores anteriores serão preservados ou se a versão vigente deverá ser aplicada com recálculo.</p>
            <div className="notice-actions">
              <Button type="button" variant="secondary" onClick={() => decideMigration('keep_previous')} disabled={busy}>Manter parâmetros e valores anteriores</Button>
              <Button type="button" onClick={() => decideMigration('update_current')} disabled={busy}>Atualizar para a versão vigente</Button>
            </div>
          </Notice>
        )}
        <Section id="proposal-responsible" title="Responsável pela proposta" description="Selecione quem responde pela elaboração desta proposta em nome da ZURC.">
          <Field id="proposal-responsible-select" label="Responsável pela proposta" required>
            <select id="proposal-responsible-select" value={inputs.proposal_responsible || ''} onChange={(event) => set('proposal_responsible', event.target.value)} required>
              <option value="">Selecione</option>
              {PROPOSAL_RESPONSIBLES.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </Field>
        </Section>
        {data.questions.sections.map((section) => (
          <Question
            key={`${section.id}-${section.field || ''}`}
            section={section}
            value={inputs[section.field || section.id]}
            inputs={inputs}
            set={set}
            result={result}
          />
        ))}
        {result?.requires_manual_review && (
          <Section
            id="manual-technical"
            title="Orçamento individual"
            description="Este caso precisa de composição individual antes da finalização."
          >
            <Field id="manual_technical_price" label="Valor técnico definido">
              <input
                id="manual_technical_price"
                type="number"
                min="0"
                step="0.01"
                value={inputs.manual_technical_price || ''}
                onChange={(event) => set('manual_technical_price', Number(event.target.value))}
              />
            </Field>
            <Notice tone="warning" title="Análise individual necessária">
              O preço total permanece indisponível até a conclusão dessa definição.
            </Notice>
          </Section>
        )}
        <FinancialAdjustmentModal
          open={financialModalOpen}
          onClose={() => setFinancialModalOpen(false)}
          proposalId={id}
          service={data.proposal.service_code}
          financial={financial}
          onUpdated={applyFinancialUpdate}
          onError={setError}
          onUpdating={setFinancialUpdating}
          openerRef={adjustButtonRef}
        />
        {result && <details className="construction-memory"><summary>Ver memória técnica da construção</summary><Memory result={result} inputs={inputs} /></details>}
      </div>
      <div className="financial-column">
        <FinancialSummary
          financial={financial}
          updating={financialUpdating}
          service={data.proposal.service_code}
          result={result}
        />
        <div className="financial-actions">
          <button ref={adjustButtonRef} type="button" className={`financial-adjust-card ${commercialConfigured ? 'is-secondary' : 'is-primary'}`} onClick={() => setFinancialModalOpen(true)}>
            <strong>Ajustar preço e pagamento</strong>
            <span>Descontos, ajuste manual, parâmetros e parcelas.</span>
          </button>
          {result && <Button variant={commercialConfigured ? 'primary' : 'secondary'} onClick={complete} disabled={busy || !commercialConfigured || manualPending || financialUpdating}>
            Concluir construção
          </Button>}
        </div>
      </div>
    </div>
  );
}

function FinancialSummary({ financial, updating, service, result }: { financial: any; updating: boolean; service: string; result: any }) {
  const value = (x: any) => (x !== null && x !== undefined && x !== '' && Number(x) !== 0 ? money(x) : null);
  const empty = !financial || financial.calculation_status === 'empty' || !result;
  const grossMode = financial?.final_value_mode === 'gross';
  const confirmed = financial?.confirmation_status === 'confirmed' || financial?.migration_status === 'kept_previous';
  const discount = Number(financial?.discount_amount || 0);
  const paymentCount = financial?.payment?.installment_count || financial?.payment?.installments?.length || 0;
  return (
    <aside className="live-summary financial-reference" aria-live="polite">
      <div className="summary-title"><span>PROPOSTA EM CONSTRUÇÃO</span><strong>{service}</strong></div>
      {updating ? <div className="financial-loading">Atualizando preço...</div> : empty ? (
        <div className="financial-empty">Nenhum cálculo disponível</div>
      ) : (
        <dl>
          {Number(financial?.area_physical_m2 || 0) > 0 && <SummaryLine label="Área física total" value={`${numberPt(financial.area_physical_m2)} m²`} />}
          <SummaryLine label="Complexidade" value={financial?.complexity} />
          <SummaryLine label="Preço técnico calculado" value={financial?.manual_pending ? 'Aguardando definição manual' : value(financial?.technical_price)} emphasis="technical" />
          {!financial?.manual_pending && <SummaryLine label="Valor líquido" value={value(financial?.liquid_total)} emphasis="net" />}
          {discount > 0 && <SummaryLine label="Desconto aplicado" value={`- ${money(discount)}`} />}
          {grossMode && <SummaryLine label="Preço bruto calculado" value={value(financial?.calculated_gross)} emphasis="gross" />}
          <SummaryLine
            label="Preço final"
            value={financial?.manual_pending ? 'Indisponível enquanto houver pendência manual' : confirmed ? value(financial?.commercial_price_confirmed) : 'Aguardando confirmação'}
            emphasis="confirmed"
          />
          {paymentCount > 0 && <SummaryLine label="Parcelamento" value={`${paymentCount} ${paymentCount === 1 ? 'parcela' : 'parcelas'}`} />}
        </dl>
      )}
    </aside>
  );
}

function SummaryLine({ label, value, emphasis }: { label: string; value: string | null | undefined; emphasis?: string }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <>
      <dt className={emphasis || ''}>{label}</dt>
      <dd className={emphasis ? `summary-${emphasis}` : ''}>{value}</dd>
    </>
  );
}

function FinancialAdjustmentModal({ open, onClose, proposalId, service, financial, onUpdated, onError, onUpdating, openerRef }: {
  open: boolean; onClose: () => void; proposalId: string; service: string; financial: any;
  onUpdated: (r: any) => void; onError: (m: string) => void; onUpdating: (v: boolean) => void;
  openerRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const busyRef = useRef(false);
  const [draft, setDraft] = useState<any>({});
  const [payment, setPayment] = useState<any>(defaultPayment());
  const [financialPreview, setFinancialPreview] = useState<any>(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { busyRef.current = busy; }, [busy]);
  useEffect(() => {
    if (!open) { initialized.current = false; return; }
    const initialOptions = clone(financial?.options || {});
    if ((initialOptions.discount?.mode || 'none') === 'none') initialOptions.discount = { mode: 'none', base: 'liquid', value: '0', show_in_pdf: false };
    setDraft(initialOptions);
    const storedPayment = clone(financial?.payment || {});
    setPayment(storedPayment?.valid && storedPayment?.installment_count ? storedPayment : defaultPayment());
    setFinancialPreview({ plan: financial?.plan || {}, price_final_selected: financial?.plan?.price_final_selected || financial?.commercial_price_confirmed || null });
    setExpanded((financial?.options?.composition_mode || 'standard') === 'custom');
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    const timer = window.setTimeout(() => dialogRef.current?.querySelector<HTMLElement>('button, input, select')?.focus(), 0);
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busyRef.current) { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const nodes = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled])'));
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', keydown);
      root?.removeAttribute('inert');
      window.setTimeout(() => openerRef.current?.focus(), 0);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      api.post<any>(`/proposals/${proposalId}/financial-preview`, { options: draft })
        .then(setFinancialPreview)
        .catch(() => undefined);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [open, proposalId, JSON.stringify(draft)]);
  if (!open) return null;
  const patch = (path: string, value: any) => setDraft((old: any) => setNested(old, path, value));
  const mode = draft.final_value_mode || 'gross';
  const composition = draft.composition_mode || 'standard';
  const discount = draft.discount || {};
  const manual = draft.manual_adjustment || {};
  const bonus = draft.s7_bonus || {};
  const plan = financialPreview?.plan || financial?.plan || {};
  const profile = plan.profile_snapshot || financial?.plan?.profile_snapshot || {};
  const differences = plan.composition_differences || [];
  const paymentMode = payment.definition_mode || 'percentage';
  const liveFinalPrice = financialPreview?.price_final_selected || plan.price_final_selected || financial?.commercial_price_confirmed || 0;
  const equalValues = equalInstallmentValues(liveFinalPrice, Number(payment.installment_count || payment.installments?.length || 1));
  const updatePaymentRow = (index: number, changes: any) => {
    const rows = [...(payment.installments || [])];
    rows[index] = { ...(rows[index] || blankInstallment(index)), ...changes };
    setPayment({ ...payment, installments: rows });
  };
  const enableCustom = () => {
    patch('composition_mode', 'custom');
    setExpanded(true);
  };
  async function confirm() {
    setBusy(true); onUpdating(true); onError('');
    try {
      const paymentToSave = paymentWithCount(payment, Number(payment.installment_count || 1));
      const state = await api.post<any>(`/proposals/${proposalId}/financial-confirm`, {
        justification: manual.justification || '',
        options: draft,
        payment: { ...paymentToSave, payment_method: paymentToSave.payment_method || 'Pix' },
      });
      onUpdated(state);
      setPayment(clone(state.payment || paymentToSave));
      onClose();
    } catch (cause: any) { onError(cause.message); }
    finally { setBusy(false); onUpdating(false); }
  }
  function restore() {
    setDraft((old: any) => ({ ...old, composition_mode: 'standard', custom_overrides: {} }));
    setExpanded(false);
  }
  return createPortal(
    <div className="modal-backdrop" role="presentation">
      <div ref={dialogRef} className="financial-modal" role="dialog" aria-modal="true" aria-labelledby="financial-modal-title">
        <header><div><span className="eyebrow">PREÇO E PAGAMENTO</span><h2 id="financial-modal-title">Ajustar preço e pagamento</h2></div><button type="button" className="icon-button" aria-label="Fechar" onClick={onClose}>×</button></header>
        <div className="financial-modal-body">
          <Section id="final-mode" title="Modo do valor final">
            <label><input type="radio" name="final-mode" checked={mode === 'gross'} onChange={() => patch('final_value_mode', 'gross')} /> Valor bruto <small>Padrão para novas propostas.</small></label>
            <label><input type="radio" name="final-mode" checked={mode === 'liquid'} onChange={() => patch('final_value_mode', 'liquid')} /> Valor líquido <small>A caixa principal não apresenta o bruto como valor alternativo vigente.</small></label>
          </Section>
          {mode === 'gross' && <Section id="gross-formation" title="Parâmetros da formação do preço bruto" description={`Perfil utilizado: ${financial?.profile_version || plan.profile_version || 'não identificado'}`}>
            <div className="composition-selector">
              <label><input type="radio" name="composition" checked={composition === 'standard'} onChange={() => patch('composition_mode', 'standard')} /> Composição padrão</label>
              <label><input type="radio" name="composition" checked={composition === 'custom'} onChange={enableCustom} /> Composição personalizada</label>
            </div>
            <dl className="parameter-readonly">
              <dt>Alíquota efetiva</dt><dd>{pct(profile.company_tax_rate || 0)}</dd>
              <dt>Reserva pretendida</dt><dd>{pct(plan.reserve_rate || profile.reserve_rate || 0)}</dd>
              {composition !== 'custom' && <><dt>Limite gerencial</dt><dd>{pct(plan.management_limit_rate || profile.management_limit_rate || 0)}</dd></>}
              <dt>Arredondamento</dt><dd>{money(plan.rounding_unit || profile.rounding_unit || 0)}</dd>
              <dt>Reserva efetiva</dt><dd>{money(plan.effective_reserve || 0)}</dd>
              <dt>Limite utilizado</dt><dd>{money(plan.management_limit_amount || 0)}</dd>
            </dl>
            {plan.reserve_reduced && <Notice tone="warning" title="Reserva reduzida">A reserva efetiva foi reduzida para respeitar o limite gerencial.</Notice>}
            {composition === 'standard' && <Button type="button" variant="secondary" onClick={enableCustom}>Alterar parâmetros</Button>}
            {expanded && composition === 'custom' && <div className="parameter-grid">
              <Field id="company-rate" label="Alíquota efetiva (%)"><input id="company-rate" value={String(Number((draft.custom_overrides?.company_tax_rate ?? profile.company_tax_rate ?? 0.06)) * 100).replace('.', ',')} onChange={(e) => patch('custom_overrides.company_tax_rate', ratioInput(e.target.value))} /></Field>
              <Field id="reserve-rate" label="Reserva pretendida (%)"><input id="reserve-rate" value={String(Number((draft.custom_overrides?.reserve_rate ?? profile.reserve_rate ?? 0.10)) * 100).replace('.', ',')} onChange={(e) => patch('custom_overrides.reserve_rate', ratioInput(e.target.value))} /></Field>
              <Field id="management-limit" label="Limite gerencial (%)"><input id="management-limit" value={String(Number((draft.custom_overrides?.management_limit_rate ?? profile.management_limit_rate ?? 0.20)) * 100).replace('.', ',')} onChange={(e) => patch('custom_overrides.management_limit_rate', ratioInput(e.target.value))} /></Field>
              <Field id="rounding-unit" label="Unidade de arredondamento"><input id="rounding-unit" value={draft.custom_overrides?.rounding_unit ?? profile.rounding_unit ?? '100'} onChange={(e) => patch('custom_overrides.rounding_unit', normalizeMoneyInput(e.target.value) || '100')} /></Field>
            </div>}
            {differences.length > 0 && <div className="composition-differences"><strong>Diferenças da composição personalizada</strong><ul>{differences.map((item:any) => <li key={item.path}>{item.path}: {String(item.standard)} → {String(item.custom)}</li>)}</ul></div>}
            {composition === 'custom' && <Button type="button" variant="secondary" onClick={restore}>Restaurar composição padrão</Button>}
          </Section>}
          {service === 'S7' && <Section id="s7-bonus" title="Bonificação de S7">
            <label><input type="checkbox" checked={Boolean(bonus.enabled)} onChange={(e) => patch('s7_bonus.enabled', e.target.checked)} /> Parecer anterior elaborado pela ZURC</label>
            <Field id="s7-bonus-value" label="Bonificação (%)"><input id="s7-bonus-value" value={bonus.percentage || '30'} onChange={(e) => patch('s7_bonus.percentage', normalizeMoneyInput(e.target.value) || '0')} /></Field>
          </Section>}
          <Section id="discount" title="Desconto comercial">
            <div className={discount.mode === 'none' || !discount.mode ? 'discount-single-field' : 'parameter-grid'}>
              <Field id="discount-mode" label="Tipo"><select id="discount-mode" value={discount.mode || 'none'} onChange={(e) => {
                const next = e.target.value;
                if (next === 'none') setDraft((old: any) => ({ ...old, discount: { mode: 'none', base: 'liquid', value: '0', show_in_pdf: false } }));
                else setDraft((old: any) => ({ ...old, discount: { ...(old.discount || {}), mode: next, base: old.discount?.base || 'liquid', value: old.discount?.value || '0', show_in_pdf: Boolean(old.discount?.show_in_pdf) } }));
              }}><option value="none">Sem desconto</option><option value="percentage">Percentual</option><option value="amount">Valor em reais</option></select></Field>
              {discount.mode && discount.mode !== 'none' && <Field id="discount-base" label="Base"><select id="discount-base" value={discount.base || 'liquid'} onChange={(e) => patch('discount.base', e.target.value)}><option value="liquid">Valor líquido</option>{mode === 'gross' && <option value="gross">Valor bruto</option>}</select></Field>}
              {discount.mode && discount.mode !== 'none' && <Field id="discount-value" label="Valor"><input id="discount-value" value={discount.value || '0'} onChange={(e) => patch('discount.value', normalizeMoneyInput(e.target.value) || '0')} /></Field>}
            </div>
            {discount.mode && discount.mode !== 'none' && <label><input type="checkbox" checked={Boolean(discount.show_in_pdf)} onChange={(e) => patch('discount.show_in_pdf', e.target.checked)} /> Exibir condição de desconto no PDF</label>}
          </Section>
          <Section id="manual-adjustment" title="Ajuste manual" description="O valor manual substitui o total completo e deve contemplar ART, deslocamento e demais componentes incluídos.">
            <label><input type="checkbox" checked={Boolean(manual.enabled)} onChange={(e) => patch('manual_adjustment.enabled', e.target.checked)} /> Substituir o resultado automático</label>
            {manual.enabled && <div className="parameter-grid">
              <Field id="manual-base" label="Base"><select id="manual-base" value={manual.base || 'liquid'} onChange={(e) => patch('manual_adjustment.base', e.target.value)}><option value="liquid">Líquida</option><option value="gross">Bruta</option></select></Field>
              <Field id="manual-value" label="Valor total"><input id="manual-value" value={manual.value || ''} onChange={(e) => patch('manual_adjustment.value', normalizeMoneyInput(e.target.value))} /></Field>
              <Field id="manual-reason" label="Justificativa, opcional"><input id="manual-reason" value={manual.justification || ''} onChange={(e) => patch('manual_adjustment.justification', e.target.value)} /></Field>
            </div>}
            {(plan.alerts || []).filter((item:string) => item.includes('ajuste manual')).map((item:string) => <Notice key={item} tone="warning" title="Alerta não bloqueante">{item}</Notice>)}
          </Section>
          <Section id="protected-costs" title="Custos protegidos">
            <label><input type="checkbox" checked={Boolean(draft.art?.included)} onChange={(e) => patch('art.included', e.target.checked)} /> ART incluída</label>
            <Field id="art-cost" label="ART"><input id="art-cost" value={draft.art?.cost || '110'} onChange={(e) => patch('art.cost', normalizeMoneyInput(e.target.value) || '0')} /></Field>
            {plan.travel_direct_net && Number(plan.travel_direct_net) > 0 && <p>Combustível, pedágio e estacionamento: {money(plan.travel_direct_net)}. Esses custos não recebem desconto nem participam da divisão interna.</p>}
          </Section>
          <Section id="payment" title="Parcelas, momentos e meio de pagamento">
            <div className="parameter-grid">
              <Field id="installment-count" label="Quantidade"><input id="installment-count" type="number" min="1" value={payment.installment_count || 0} onChange={(e) => setPayment(paymentWithCount(payment, Number(e.target.value)))} /></Field>
              <Field id="payment-definition" label="Definir parcelas por"><select id="payment-definition" value={paymentMode} onChange={(e) => setPayment({...payment,definition_mode:e.target.value})}><option value="percentage">Percentuais</option><option value="amount">Valores</option><option value="equal">Parcelas iguais</option><option value="custom">Personalizado</option></select></Field>
              <Field id="payment-method" label="Meio"><select id="payment-method" value={payment.payment_method || 'Pix'} onChange={(e) => setPayment({ ...payment, payment_method: e.target.value })}><option>Pix</option><option>transferência bancária</option></select></Field>
            </div>
            <div className="payment-editor-list">{(payment.installments || []).map((row:any,index:number)=>(
              <div className="payment-editor-row" key={index}>
                <Field id={`installment-label-${index}`} label={`Parcela ${index+1}`}><input id={`installment-label-${index}`} value={row.label || `Parcela ${index+1}`} onChange={(e)=>updatePaymentRow(index,{label:e.target.value})} /></Field>
                {paymentMode !== 'equal' && <Field id={`installment-value-${index}`} label={paymentMode==='percentage'?'Percentual':'Valor'}><input id={`installment-value-${index}`} value={paymentMode==='percentage'?(row.percentage||''):(row.amount||'')} onChange={(e)=>updatePaymentRow(index,{[paymentMode==='percentage'?'percentage':'amount']:normalizeMoneyInput(e.target.value)})} /></Field>}
                {paymentMode === 'equal' && <Field id={`installment-equal-${index}`} label="Valor derivado"><input id={`installment-equal-${index}`} value={equalValues[index] || money(0)} readOnly /></Field>}
                <Field id={`installment-moment-${index}`} label="Momento"><select id={`installment-moment-${index}`} value={row.moment_type || 'approval'} onChange={(e)=>updatePaymentRow(index,{moment_type:e.target.value})}><option value="approval">Aprovação da proposta</option><option value="signature">Assinatura</option><option value="service_start">Início dos serviços</option><option value="preliminary_delivery">Entrega preliminar</option><option value="compatibility">Compatibilização</option><option value="final_delivery">Entrega final</option><option value="days_after_event">Dias após evento</option><option value="specific_date">Data específica</option><option value="stage_delivery">Entrega de etapa</option><option value="custom">Personalizado</option></select></Field>
                {row.moment_type==='days_after_event' && <><Field id={`installment-days-${index}`} label="Quantidade de dias"><input id={`installment-days-${index}`} type="number" min="0" value={row.moment_days||''} onChange={(e)=>updatePaymentRow(index,{moment_days:e.target.value})} /></Field><Field id={`installment-event-${index}`} label="Evento"><input id={`installment-event-${index}`} value={row.moment_event||''} onChange={(e)=>updatePaymentRow(index,{moment_event:e.target.value})} /></Field></>}
                {row.moment_type==='specific_date' && <Field id={`installment-date-${index}`} label="Data"><input id={`installment-date-${index}`} type="date" value={row.moment_date||''} onChange={(e)=>updatePaymentRow(index,{moment_date:e.target.value})} /></Field>}
                {row.moment_type==='stage_delivery' && <Field id={`installment-stage-${index}`} label="Etapa"><input id={`installment-stage-${index}`} value={row.moment_stage||''} onChange={(e)=>updatePaymentRow(index,{moment_stage:e.target.value})} /></Field>}
                {row.moment_type==='custom' && <Field id={`installment-custom-${index}`} label="Texto personalizado"><input id={`installment-custom-${index}`} value={row.moment_text||''} onChange={(e)=>updatePaymentRow(index,{moment_text:e.target.value})} /></Field>}
              </div>
            ))}</div>
            {(financial?.payment?.errors || []).map((item:string) => <Notice key={item} tone="warning" title="Condição de pagamento incompleta">{item}</Notice>)}
          </Section>
        </div>
        <footer><Button type="button" variant="secondary" onClick={onClose}>Fechar</Button><Button type="button" onClick={confirm} disabled={busy}>{busy ? 'Confirmando...' : 'Confirmar preço'}</Button></footer>
      </div>
    </div>, document.body,
  );
}

function Question({
  section,
  value,
  inputs,
  set,
  result,
}: {
  section: QuestionSection;
  value: any;
  inputs: any;
  set: (key: string, value: any) => void;
  result: any;
}) {
  const key = section.field || section.id;
  const options = (section.options || []).map(normalizeOption).filter((option) => option.id && option.label);
  if (section.type === 'deadline')
    return (
      <Section id="deadline" title="Prazo estimado" description="A modalidade de prazo altera o preço técnico por um único fator não acumulativo.">
        <div className="form-grid">
          <Field id="deadline-mode" label="Condição comercial do prazo">
            <select id="deadline-mode" value={inputs.deadline_mode || 'normal'} onChange={(event) => set('deadline_mode', event.target.value)}>
              <option value="normal">Prazo normal · fator 1,00</option>
              <option value="reduced">Prazo reduzido · fator 1,10</option>
              <option value="urgent">Prazo urgente · fator 1,25</option>
            </select>
          </Field>
          <Field id="deadline_days" label="Quantidade de dias">
            <input id="deadline_days" type="number" min="1" step="1" value={inputs.deadline_days || ''} onChange={(event) => set('deadline_days', Math.max(1, Number(event.target.value)))} />
          </Field>
          <Field id="deadline-basis" label="Contagem e marco inicial">
            <select id="deadline-basis" value={inputs.deadline_basis || 'business_after_compatibility'} onChange={(event) => set('deadline_basis', event.target.value)}>
              <option value="business_after_compatibility">Dias úteis após a compatibilização</option>
              <option value="business">Dias úteis</option>
              <option value="calendar">Dias corridos</option>
              <option value="calendar_after_compatibility">Dias corridos após a compatibilização</option>
            </select>
          </Field>
        </div>
      </Section>
    );
  if (section.type === 'meetings')
    return (
      <Section id="meetings" title="Reuniões">
        <div className="form-grid">
          <Field id="included_meetings" label="Reuniões incluídas">
            <input
              id="included_meetings"
              type="number"
              min="0"
              step="1"
              value={inputs.included_meetings ?? 0}
              onChange={(event) => set('included_meetings', Math.max(0, Number(event.target.value)))}
            />
          </Field>
          <Field id="meetings_observation" label="Observação, opcional">
            <input
              id="meetings_observation"
              value={inputs.meetings_observation || ''}
              onChange={(event) => set('meetings_observation', event.target.value)}
            />
          </Field>
        </div>
        <label className="toggle-card">
          <input
            type="checkbox"
            checked={Boolean(inputs.include_meetings_in_document)}
            onChange={(event) => set('include_meetings_in_document', event.target.checked)}
          />
          <span>
            <strong>Incluir esta condição na proposta</strong>
            <small>Desativado por padrão. A quantidade permanece registrada para planejamento interno.</small>
          </span>
        </label>
      </Section>
    );
  if (section.type === 'revisions') {
    const suggested = Number(result?.details?.physical_area_m2 || 0) > 1500 ? 3 : 2;
    return (
      <Section id="revisions" title="Revisões" description={`Padrão sugerido pela área do projeto: ${suggested} + 1`}>
        <div className="form-grid">
          <Field id="revisions_before" label="Revisões antes da compatibilização">
            <input
              id="revisions_before"
              type="number"
              min="0"
              step="1"
              value={inputs.revisions_before ?? suggested}
              onChange={(event) => {
                set('revisions_before', Math.max(0, Number(event.target.value)));
                set('revisions_manual', true);
              }}
            />
          </Field>
          <Field id="revisions_after" label="Ajustes pontuais após a compatibilização">
            <input
              id="revisions_after"
              type="number"
              min="0"
              step="1"
              value={inputs.revisions_after ?? 1}
              onChange={(event) => {
                set('revisions_after', Math.max(0, Number(event.target.value)));
                set('revisions_manual', true);
              }}
            />
          </Field>
        </div>
        {inputs.revisions_manual && Number(inputs.revisions_before) !== suggested && (
          <Notice tone="warning" title={`A área do projeto passou para uma faixa cujo padrão sugerido é ${suggested} + 1.`}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                set('revisions_before', suggested);
                set('revisions_after', 1);
                set('revisions_manual', false);
              }}
            >
              Aplicar padrão sugerido
            </Button>
          </Notice>
        )}
      </Section>
    );
  }
  if (section.type === 'visits')
    return (
      <Section id="visits" title="Visitas técnicas">
        <div className="form-grid">
          <Field id="included_visits" label="Quantidade incluída">
            <input id="included_visits" type="number" min="0" step="1" value={inputs.included_visits ?? 0} onChange={(event) => set('included_visits', Math.max(0, Number(event.target.value)))} />
          </Field>
          <Field id="included_visit_hours" label="Horas incluídas">
            <input id="included_visit_hours" type="number" min="0" step="0.5" value={inputs.included_visit_hours ?? 0} onChange={(event) => set('included_visit_hours', Math.max(0, Number(event.target.value)))} />
          </Field>
        </div>
        <label className="toggle-card">
          <input type="checkbox" checked={Boolean(inputs.include_visits_in_document)} onChange={(event) => set('include_visits_in_document', event.target.checked)} />
          <span><strong>Exibir no PDF</strong><small>A visita somente aparece no documento quando esta opção estiver selecionada.</small></span>
        </label>
      </Section>
    );
  if (section.type === 'floor_groups')
    return (
      <FloorGroups
        rows={inputs.floor_groups || []}
        setRows={(rows) => set('floor_groups', rows)}
        calculated={result?.details?.groups || []}
        generalObservation={inputs.floor_groups_observations || ''}
        setGeneralObservation={(text) => set('floor_groups_observations', text)}
      />
    );
  if (section.type === 'reinforcement_groups')
    return (
      <ReinforcementGroups
        rows={inputs.reinforcement_groups || []}
        setRows={(rows) => set('reinforcement_groups', rows)}
        calculated={result?.details?.reinforcement_groups || []}
      />
    );
  if (section.type === 'document_options')
    return (
      <Section id={`q-${key}`} title={section.label} description="Itens documentais condicionais só entram em Entregáveis quando selecionados.">
        <div className="answer-list checkbox-grid">
          {options.map((option) => (
            <label key={option.id} className={Boolean(inputs[option.id]) ? 'selected' : ''}>
              <input type="checkbox" checked={Boolean(inputs[option.id])} onChange={(event) => set(option.id, event.target.checked)} />
              <span><strong>{option.label}</strong></span>
            </label>
          ))}
        </div>
      </Section>
    );
  if (section.type === 'radio')
    return (
      <Section id={`q-${key}`} title={section.group || section.label}>
        <div className="answer-list radio-list">
          {options.map((option) => (
            <label key={option.id} className={value === option.id ? 'selected' : ''}>
              <input type="radio" name={key} checked={value === option.id} onChange={() => set(key, option.id)} />
              <span>
                <strong>{option.label}</strong>
              </span>
            </label>
          ))}
        </div>
      </Section>
    );
  if (section.type === 'checkboxes')
    return (
      <Section id={`q-${key}`} title={section.label}>
        <div className="answer-list checkbox-grid">
          {options.map((option) => (
            <label key={option.id} className={(value || []).includes(option.id) ? 'selected' : ''}>
              <input
                type="checkbox"
                checked={(value || []).includes(option.id)}
                onChange={(event) =>
                  set(
                    key,
                    event.target.checked ? [...(value || []), option.id] : (value || []).filter((item: string) => item !== option.id),
                  )
                }
              />
              <span>
                <strong>{option.label}</strong>
              </span>
            </label>
          ))}
        </div>
      </Section>
    );
  if (section.type === 'boolean') {
    if (key === 's5_distinct_efforts_confirmed' && (inputs.s5_analysis_items || []).length <= 1) return null;
    return (
      <Section id={`q-${key}`} title={section.label}>
        <div className="segmented">
          <button type="button" className={value === false ? 'selected' : ''} onClick={() => set(key, false)}>
            Não
          </button>
          <button type="button" className={value === true ? 'selected' : ''} onClick={() => set(key, true)}>
            Sim
          </button>
        </div>
        {key === 'outside_salvador' && value && (
          <div className="form-grid compact travel-grid">
            <Field id="distance" label="Distância de ida (km)"><input id="distance" type="number" min="0" step="0.1" value={inputs.distance_one_way_km || ''} onChange={(event) => set('distance_one_way_km', Number(event.target.value))} /></Field>
            <Field id="travel_hours_total" label="Horas totais de viagem"><input id="travel_hours_total" type="number" min="0" step="0.5" value={inputs.travel_hours_total || ''} onChange={(event) => set('travel_hours_total', Number(event.target.value))} /></Field>
            <Field id="vehicle-consumption" label="Consumo do veículo (km/L)"><input id="vehicle-consumption" type="number" min="0.1" step="0.1" value={inputs.vehicle_consumption_km_l || ''} onChange={(event) => set('vehicle_consumption_km_l', Number(event.target.value))} /></Field>
            <Field id="gasoline-price" label="Gasolina (R$/L)"><input id="gasoline-price" type="number" min="0" step="0.01" value={inputs.gasoline_brl_l || ''} onChange={(event) => set('gasoline_brl_l', Number(event.target.value))} /></Field>
            <Field id="fuel-value" label="Combustível calculado ou informado"><input id="fuel-value" type="number" min="0" step="0.01" value={inputs.fuel || ''} onChange={(event) => set('fuel', event.target.value === '' ? '' : Number(event.target.value))} /></Field>
            <Field id="toll" label="Pedágio"><input id="toll" type="number" min="0" step="0.01" value={inputs.toll || ''} onChange={(event) => set('toll', Number(event.target.value))} /></Field>
            <Field id="parking" label="Estacionamento"><input id="parking" type="number" min="0" step="0.01" value={inputs.parking || ''} onChange={(event) => set('parking', Number(event.target.value))} /></Field>
          </div>
        )}
      </Section>
    );
  }
  if (section.type === 'percentage') return null;
  return (
    <Section id={`q-${key}`} title={section.label}>
      <Field id={key} label={section.label}>
        <input
          id={key}
          type="number"
          min={section.min ?? 0}
          step="1"
          value={value ?? ''}
          onChange={(event) => set(key, Number(event.target.value))}
        />
      </Field>
    </Section>
  );
}

function groupedCoefficients(area: number, coefficients: any[]) {
  const values = coefficients.map(Number);
  const groups: { from: number; to: number; coefficient: number }[] = [];
  values.forEach((coefficient, index) => {
    const last = groups.at(-1);
    if (last && last.coefficient === coefficient) last.to = index + 1;
    else groups.push({ from: index + 1, to: index + 1, coefficient });
  });
  return groups.map((group) => ({
    label: group.from === group.to ? `${group.from}º pavimento` : `${group.from}º ao ${group.to}º pavimento`,
    formula:
      group.from === group.to
        ? `${numberPt(area)} × ${numberPt(group.coefficient)} = ${numberPt(area * group.coefficient)} m²`
        : `${group.to - group.from + 1} × ${numberPt(area)} × ${numberPt(group.coefficient)} = ${numberPt((group.to - group.from + 1) * area * group.coefficient)} m²`,
  }));
}

function FloorGroups({
  rows,
  setRows,
  calculated,
  generalObservation,
  setGeneralObservation,
}: {
  rows: any[];
  setRows: (rows: any[]) => void;
  calculated: any[];
  generalObservation: string;
  setGeneralObservation: (value: string) => void;
}) {
  function patch(index: number, key: string, value: any) {
    setRows(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
  }
  return (
    <Section
      id="floors"
      title="Pavimentos e grupos"
      description="Informe a área unitária e a quantidade. A regra histórica efetivamente implementada é aplicada sem expor coeficientes no documento comercial."
    >
      <div className="table-scroll">
        <table className="input-table compact-floor-table">
          <thead>
            <tr>
              <th>Pavimento ou grupo</th>
              <th>Área unitária</th>
              <th>Quantidade</th>
              <th>Repetições</th>
              <th>Regra de repetição</th>
              <th>Área física</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const calculation = calculated[index] || {};
              const repeated = Number(row.quantity || 1) > 1;
              return (
                <React.Fragment key={index}>
                  <tr>
                    <td data-label="Pavimento ou grupo">
                      <input
                        aria-label={`Nome do grupo ${index + 1}`}
                        value={row.name}
                        onChange={(event) => patch(index, 'name', event.target.value)}
                      />
                      <button
                        type="button"
                        className="row-note-action"
                        onClick={() => patch(index, 'show_observation', !row.show_observation)}
                      >
                        {row.show_observation ? 'Ocultar observação' : 'Adicionar observação'}
                      </button>
                    </td>
                    <td data-label="Área unitária">
                      <input
                        aria-label={`Área ${index + 1}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.area_m2 || ''}
                        onChange={(event) => patch(index, 'area_m2', Number(event.target.value))}
                      />
                    </td>
                    <td data-label="Quantidade">
                      <input
                        aria-label={`Quantidade ${index + 1}`}
                        type="number"
                        min="1"
                        step="1"
                        value={row.quantity || 1}
                        onChange={(event) => patch(index, 'quantity', Math.max(1, Number(event.target.value)))}
                      />
                    </td>
                    <td data-label="Repetições">
                      <strong>{repeated ? 'Sim' : 'Não'}</strong>
                    </td>
                    <td data-label="Regra de repetição">
                      {repeated ? <>
                        <select
                          aria-label={`Regra de repetição ${index + 1}`}
                          value={row.repetition_type || 'none'}
                          onChange={(event) => patch(index, 'repetition_type', event.target.value)}
                        >
                          <option value="none">Sem redução</option>
                          <option value="progressive">Com redução progressiva</option>
                        </select>
                        <small className="rule-description">
                          {(row.repetition_type || 'none') === 'progressive' ? REPETITION_DESCRIPTION : NO_REDUCTION_DESCRIPTION}
                        </small>
                      </> : <span className="not-applicable">Não aplicável</span>}
                    </td>
                    <td data-label="Área física">
                      {calculation.physical_area_m2 !== undefined ? `${numberPt(calculation.physical_area_m2)} m²` : '—'}
                    </td>
                    <td data-label="Ações">
                      <button
                        type="button"
                        className="icon-button"
                        aria-label="Remover grupo"
                        onClick={() => setRows(rows.length === 1 ? [blankFloor()] : rows.filter((_, rowIndex) => rowIndex !== index))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                  {row.show_observation && (
                    <tr className="row-observation">
                      <td colSpan={7}>
                        <Field id={`floor-observation-${index}`} label={`Observação específica de ${row.name || `grupo ${index + 1}`}`}>
                          <textarea
                            id={`floor-observation-${index}`}
                            value={row.observations || ''}
                            onChange={(event) => patch(index, 'observations', event.target.value)}
                          />
                        </Field>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="secondary" onClick={() => setRows([...rows, blankFloor()])}>
        Adicionar pavimento ou grupo
      </Button>
      <details className="equivalent-memory">
        <summary>Ver cálculo da área equivalente</summary>
        {rows.map((row, index) => {
          const calculation = calculated[index] || {};
          const bands = groupedCoefficients(Number(row.area_m2 || 0), calculation.coefficients || []);
          return (
            <article key={index}>
              <h4>{row.name || `Grupo ${index + 1}`}</h4>
              <p>
                Área unitária: {numberPt(row.area_m2)} m² · Quantidade: {row.quantity || 1} · Regra:{' '}
                {calculation.repetition_rule?.label || ((row.repetition_type || 'none') === 'progressive' ? 'Com redução progressiva' : 'Sem redução')}
              </p>
              {bands.map((band, bandIndex) => (
                <div key={bandIndex} className="equivalent-line">
                  <span>{band.label}</span>
                  <code>{band.formula}</code>
                </div>
              ))}
              <strong>
                Área física: {numberPt(calculation.physical_area_m2)} m² · Área equivalente: {numberPt(calculation.equivalent_area_m2)} m²
              </strong>
            </article>
          );
        })}
      </details>
      <Field id="floor-general-observation" label="Observações sobre pavimentos e áreas">
        <textarea
          id="floor-general-observation"
          value={generalObservation}
          onChange={(event) => setGeneralObservation(event.target.value)}
        />
      </Field>
    </Section>
  );
}

function ReinforcementGroups({ rows, setRows, calculated }: { rows: any[]; setRows: (rows: any[]) => void; calculated: any[] }) {
  function patch(index: number, key: string, value: any) {
    setRows(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
  }
  return (
    <Section id="reinforcement-groups" title="Grupos de elementos semelhantes">
      <div className="table-scroll">
        <table className="input-table">
          <thead>
            <tr>
              <th>Grupo</th>
              <th>Tipo de elemento</th>
              <th>Quantidade</th>
              <th>Subtotal</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <input value={row.name} onChange={(event) => patch(index, 'name', event.target.value)} />
                </td>
                <td>
                  <input value={row.element_type} onChange={(event) => patch(index, 'element_type', event.target.value)} />
                </td>
                <td>
                  <input
                    type="number"
                    min="1"
                    value={row.quantity || 1}
                    onChange={(event) => patch(index, 'quantity', Math.max(1, Number(event.target.value)))}
                  />
                </td>
                <td>{money(calculated[index]?.price)}</td>
                <td>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setRows(rows.length === 1 ? [blankGroup()] : rows.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="secondary" onClick={() => setRows([...rows, blankGroup()])}>
        Adicionar grupo
      </Button>
    </Section>
  );
}

function Memory({ result, inputs }: { result: any; inputs: any }) {
  const d = result?.details || {},
    trace = result?.trace || [];
  const pending = Boolean(d.manual_component_pending || (result?.requires_manual_review && !result?.manual_definition_completed));
  const priceMinimum = Number(d.minimum_price || result?.minimum_price || 0);
  const composition = Number(d.composition_price || result?.technical_price || 0);
  const adopted = Number(d.adopted_technical_price || result?.technical_price || 0);
  const groups = Array.isArray(d.groups) ? d.groups : [];
  const hasRepetition = groups.some(
    (group: any) =>
      Number(group.quantity || 0) > 1 &&
      group.repetition_rule?.applied !== 'none' &&
      (group.coefficients || []).some((coefficient: any) => Number(coefficient) !== 1),
  );
  const raw = trace.map((x: any) => String(x.formula || '')).join(' ');
  const baseTechnical = Number(d.base_technical_adopted ?? adopted ?? 0);
  const baseOrigin = String(d.base_technical_origin || (priceMinimum >= composition ? 'Valor mínimo / piso' : 'Cálculo pela área'));
  const deadlineFactor = Number(d.deadline_factor || 1);
  const deadlineEffect = Number(d.deadline_financial_effect || 0);
  const explanation = pending
    ? 'A composição ainda depende de definição manual. O preço calculado será apresentado somente depois da conclusão dessa definição.'
    : priceMinimum > composition
      ? 'O valor-base do serviço é superior ao resultado da composição e foi adotado como preço calculado.'
      : 'O resultado da composição é superior ao valor-base do serviço e foi adotado como preço calculado.';
  return (
    <div className="memory-panel">
      <h3>Memória de cálculo</h3>
      <section>
        <h4>Dados do projeto</h4>
        <dl>
          <dt>Área física total</dt>
          <dd>{d.physical_area_m2 !== undefined ? `${numberPt(d.physical_area_m2)} m²` : '—'}</dd>
          <dt>Área equivalente interna</dt>
          <dd>{d.equivalent_area_m2 !== undefined ? `${numberPt(d.equivalent_area_m2)} m²` : '—'}</dd>
          <dt>Área do piso 0</dt>
          <dd>{inputs.floor0_area_m2 ? `${numberPt(inputs.floor0_area_m2)} m²` : '—'}</dd>
          <dt>Quantidade de pavimentos</dt>
          <dd>{(inputs.floor_groups || []).reduce((a: number, x: any) => a + Number(x.quantity || 0), 0) || '—'}</dd>
          <dt>Regra de repetição</dt>
          <dd>{hasRepetition ? 'Com redução progressiva' : 'Sem redução'}</dd>
          <dt>Complexidade</dt>
          <dd>{d.complexity_label || d.superstructure_label || '—'}</dd>
        </dl>
      </section>
      <section>
        <h4>Parâmetros aplicados</h4>
        <dl>
          <dt>Base técnica adotada</dt>
          <dd>{money(baseTechnical)}</dd>
          <dt>Origem</dt>
          <dd>{baseOrigin}</dd>
          {deadlineFactor !== 1 && Math.abs(deadlineEffect) > 0.0001 && <>
            <dt>Prazo</dt>
            <dd>{`Fator ${deadlineFactor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · ${deadlineEffect >= 0 ? '+' : '-'} ${money(Math.abs(deadlineEffect))}`}</dd>
          </>}
          {trace
            .filter((x: any) => x.rule_id !== 'PRA-001' && !String(x.formula || '').includes('max('))
            .slice(0, 8)
            .map((x: any) => (
              <React.Fragment key={x.rule_id}>
                <dt>{x.label}</dt>
                <dd>{typeof x.result === 'number' ? money(x.result) : String(x.result ?? '—')}</dd>
              </React.Fragment>
            ))}
        </dl>
      </section>
      <section>
        <h4>Composição do preço</h4>
        <dl>
          {d.superstructure_component_status === 'Pendente' && (
            <>
              <dt>Superestrutura, definição manual</dt>
              <dd>Pendente</dd>
              <dt>Fundações rasas calculadas</dt>
              <dd>{money(d.shallow_component_price)}</dd>
            </>
          )}
          <dt>Cálculo pela composição</dt>
          <dd>{money(composition)}</dd>
          <dt>{d.value_base_label || 'Valor-base do serviço'}</dt>
          <dd>{money(priceMinimum)}</dd>
          <dt>Preço calculado adotado</dt>
          <dd>{pending ? 'Pendente' : money(adopted)}</dd>
        </dl>
        <p>{explanation}</p>
      </section>
      <details>
        <summary>Auditoria técnica</summary>
        <pre>{raw}</pre>
        <pre>{JSON.stringify(inputs, null, 2)}</pre>
      </details>
    </div>
  );
}
